import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SimProvider, useSim } from "@/context/SimContext";
import { AcademyProvider, useAcademy } from "@/context/AcademyContext";
import { StatusDot } from "@/components/ui-kit";
import DemoDrawer from "@/components/DemoDrawer";
import DecisionModal from "@/components/DecisionModal";
import {
  LayoutDashboard, MonitorPlay, ListChecks, UserCheck, Server,
  BarChart3, ScrollText, Network, Settings as SettingsIcon,
  LogOut, SlidersHorizontal, GraduationCap, BookOpen, Activity,
} from "lucide-react";
import { NGX_LOGO } from "@/lib/brand";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/live", label: "Live Control", icon: MonitorPlay },
  { to: "/tasks", label: "Tasks", icon: ListChecks },
  { to: "/decisions", label: "Human Decisions", icon: UserCheck },
  { to: "/systems", label: "Systems", icon: Server },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/events", label: "Event Log", icon: ScrollText },
  { to: "/architecture", label: "Architecture", icon: Network },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

function fmtDuration(s) {
  if (s == null) return "00:00:00";
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${sec}`;
}

function TopBar({ onOpenDemo }) {
  const { state, wsStatus } = useSim();
  const safety = state?.safety_paused;
  const wsMap = {
    connected: { label: "LIVE · CONNECTED", cls: "text-emerald-400", dot: "bg-emerald-500", pulse: true },
    reconnecting: { label: "LIVE · RECONNECTING", cls: "text-amber-400", dot: "bg-amber-500", pulse: true },
    connecting: { label: "CONNECTING…", cls: "text-amber-400", dot: "bg-amber-500", pulse: true },
    lost: { label: "CONNECTION LOST", cls: "text-red-400", dot: "bg-red-500", pulse: false },
  };
  const ws = wsMap[wsStatus] || wsMap.connecting;
  return (
    <header className="h-16 border-b border-slate-800 bg-[#070E1A] flex items-center justify-between px-6 shrink-0">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2 text-amber-400" data-testid="simulation-mode-indicator">
          <span className="h-2 w-2 rounded-full bg-amber-500 ngx-pulse" />
          <span className="text-xs font-semibold uppercase tracking-widest">Simulation Mode</span>
        </div>
        <div className={`flex items-center gap-2 ${ws.cls}`} data-testid="ws-status">
          <span className={`h-2 w-2 rounded-full ${ws.dot} ${ws.pulse ? "ngx-pulse" : ""}`} />
          <span className="text-xs font-semibold uppercase tracking-widest font-mono">{ws.label}</span>
        </div>
        {safety && (
          <div className="flex items-center gap-2 text-red-400 px-3 py-1 rounded-sm bg-red-950/50 border border-red-800" data-testid="safety-banner">
            <span className="h-2 w-2 rounded-full bg-red-500 ngx-pulse" />
            <span className="text-xs font-bold uppercase tracking-widest">Operation Paused</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-6">
        <div className="hidden md:flex items-center gap-5 font-mono text-xs text-slate-400">
          <span>LATENCY <span className="text-emerald-400">{state?.latency_ms ?? "--"}ms</span></span>
          <span>SESSION <span className="text-slate-200">{fmtDuration(state?.session_duration)}</span></span>
        </div>
        <button
          onClick={onOpenDemo} data-testid="open-demo-drawer"
          className="flex items-center gap-2 h-10 px-4 rounded-md border border-slate-700 bg-[#0A1128] hover:border-blue-500 text-slate-300 text-sm transition-colors"
          title="Demo Control Panel"
        >
          <SlidersHorizontal className="h-4 w-4" /> Demo
        </button>
      </div>
    </header>
  );
}

function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };
  return (
    <aside className="w-64 shrink-0 bg-[#070E1A] border-r border-slate-800 flex flex-col">
      <div className="h-16 flex items-center px-4 border-b border-slate-800">
        <img src={NGX_LOGO} alt="NGX Human Control Station" className="h-14 w-auto object-contain" data-testid="sidebar-logo" />
      </div>
      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to} to={to} end={end} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 h-11 rounded-md text-sm font-medium transition-colors relative ${
                isActive
                  ? "bg-blue-600/15 text-blue-300 border-l-2 border-blue-500"
                  : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/40 border-l-2 border-transparent"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" /> {label}
          </NavLink>
        ))}
        <AcademyNav />
      </nav>
      <div className="p-3 border-t border-slate-800 space-y-3">
        <NavLink to="/health" data-testid="open-neunexus-health"
          className="flex items-center gap-3 px-3 h-11 rounded-md bg-teal-600/10 border border-teal-800/60 text-teal-300 hover:bg-teal-600/20 transition-colors">
          <Activity className="h-[18px] w-[18px]" />
          <span className="flex-1 text-sm font-medium leading-tight">
            NeuNexus Health
            <span className="block text-[10px] uppercase tracking-widest text-teal-500/70">Open Demo →</span>
          </span>
        </NavLink>
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-md bg-[#0A1128] border border-slate-800">
          <div className="h-9 w-9 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-200">
            {(user?.name || "OP").slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-100 truncate">{user?.name || "Operator"}</div>
            <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
              <StatusDot tone="success" className="h-2 w-2" /> {user?.level || "Level 2"} · Connected
            </div>
          </div>
          <button onClick={doLogout} data-testid="logout-btn" className="text-slate-500 hover:text-red-400 transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}

function AcademyNav() {
  const { overallPct } = useAcademy();
  return (
    <div className="mt-4 pt-4 border-t border-slate-800">
      <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-600">
        Learning Environment
      </div>
      <NavLink to="/academy" end data-testid="nav-ngx-academy"
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 h-11 rounded-md text-sm font-medium transition-colors ${
            isActive ? "bg-blue-600/15 text-blue-300 border-l-2 border-blue-500"
              : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/40 border-l-2 border-transparent"
          }`
        }>
        <GraduationCap className="h-[18px] w-[18px]" />
        <span className="flex-1">NGX Academy</span>
        <span className="text-[10px] font-mono text-slate-500">{overallPct}%</span>
      </NavLink>
      <NavLink to="/academy/glossary" data-testid="nav-glossary"
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 h-11 rounded-md text-sm font-medium transition-colors ${
            isActive ? "bg-blue-600/15 text-blue-300 border-l-2 border-blue-500"
              : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/40 border-l-2 border-transparent"
          }`
        }>
        <BookOpen className="h-[18px] w-[18px]" /> Glossary
      </NavLink>
    </div>
  );
}

function LayoutInner() {
  const [demoOpen, setDemoOpen] = useState(false);
  return (
    <div className="h-screen flex bg-[#050B14] text-slate-100 overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar onOpenDemo={() => setDemoOpen(true)} />
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
      <DemoDrawer open={demoOpen} onClose={() => setDemoOpen(false)} />
      <DecisionModal />
    </div>
  );
}

export default function AppLayout() {
  return (
    <SimProvider>
      <AcademyProvider>
        <LayoutInner />
      </AcademyProvider>
    </SimProvider>
  );
}
